import { React, useEffect, useState } from 'react';
import axios from '../../utils/axiosInterceptor';
import { useSelector } from "react-redux";
import { useNavigate, Link } from 'react-router-dom';
import { DateTime } from 'luxon';
import { LazyLoadImage } from 'react-lazy-load-image-component';

import {
    Container, Box, Typography, Divider, Grid, Paper, Button, FormControl, TextField, Fab, Chip,
    TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';

import {
    DataGrid,
    gridPageCountSelector,
    GridPagination,
    useGridApiContext,
    GridToolbar,
    useGridSelector,
} from '@mui/x-data-grid';
import MuiPagination from '@mui/material/Pagination';

import CustomMessage from '../../utils/components/CustomMessage';
import CustomChip from '../../utils/components/CustomChip';

import { formatMoney, getDate } from '../../utils/functions/Utils';
import useWindowDimensions from '../../utils/functions/useWindowDimensions';
import LocaleTextDataGrid from '../../utils/functions/LocaleTextDataGrid';

const TablaProductos = (props) => {

    const auth = useSelector((state) => state.auth);
    let navigate = useNavigate();

    const [data, setData] = useState([]); // for table
    const [categorias, setCategorias] = useState([
        { nombre: 'Todo', value: '...' },
    ]);

    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);

    const [showDetails, setShowDetails] = useState(false);

    const [cotizacion, setCotizacion] = useState(false);

    const [requestDate, setRequestDate] = useState(DateTime.now());

    const [showVerification, setShowVerification] = useState(false);
    const [currentId, setCurrentId] = useState('');

    const [dimensiones, setDimensiones] = useState('');

    const [apiRes, setApiRes] = useState({ show: false, message: '', severity: '' }); // for custom alert message
    const { height } = useWindowDimensions();

    useEffect(() => {
        if (!['Admin', 'Vendedor', 'Tecnico', 'Almacen', 'Secretaria', 'Ayudante'].includes(auth.user.rol)) {
            navigate('/dashboard');
        }
        document.title = 'Buscador de productos';
    }, [auth])

    // for custom alert message
    const handleCloseMessage = (show) => {
        setApiRes(state => { return { ...state, show: show } })
    }

    const handleAdd = (id) => {
        setCurrentId(id);
        const product = searchResults.find(product => product.product._id === id);
        if (product.product.cantidad > 0) {
            setShowVerification(false);
            props.onAddProduct(product);
            setCurrentId('');
        } else {
            setShowVerification(true);
        }
    }

    const forceAddProduct = (id) => {
        const product = searchResults.find(product => product.product._id === id);
        props.onAddProduct(product);
        setShowVerification(false);
        setCurrentId('');
    }

    useEffect(() => {
        if (props.cotizacion) {
            setCotizacion(true);
        }
    }, [props])

    useEffect(() => {
        getData();
    }, [])

    const getData = async () => {
        try {
            const res = await axios.get(`/api/configs/company/${auth.user.company}`);
            if (res.status === 200) {
                let { categorias, buscador } = res.data;
                if (buscador.length > 0) {
                    buscador.forEach(b => {
                        categorias.push({ nombre: b.nombre, value: b.value });
                    })
                }
                setCategorias(categorias);
            }
        } catch (error) {
            setApiRes(state => { return { ...state, show: true, message: 'Ocurrió un error al obtener las categorías de producto, intentelo mas tarde', severity: 'error' } });
            console.log(error);
        }
    }

    const getProducts = async (text) => {
        try {
            const res = await axios.post('/api/products/filter', { data: text });
            if (res.status === 200) {
                setSearchResults(res.data);
                setRequestDate(DateTime.now());
            }
        } catch (error) {
            setApiRes(state => { return { ...state, show: true, message: 'Ocurrió un error al obtener los productos, intentelo mas tarde', severity: 'error' } });
            console.log(error);
        }
    }

    const handleShowDetails = async (id) => {
        try {
            const res = await axios.get(`/api/products/${id}`);
            if (res.status === 200) {
                setData(res.data);
                setShowDetails(true);
            }
        } catch (err) {
            setApiRes(state => { return { ...state, show: true, message: 'Ocurrió un error al cargar el producto, favor de intentarlo mas tarde', severity: 'error' } });
            console.log(err);
        }
    }

    const renderProducts = () => {
        return (
            <>
                <Typography variant='body1' sx={{ margin: '0px 8px', color: 'comp.gray' }}>{searchResults.length} productos encontrados</Typography>
                <Typography variant='body1' sx={{ margin: '0px 8px', color: 'comp.gray' }}>Consulta: {getDate(requestDate, 'DD, HH:mm:ss')} hrs</Typography>
                <DataGrid
                    autoPageSize={true}
                    rows={searchResults.map(({ product }) => product)}
                    density='comfortable'
                    sx={{ height: `${props.height ? props.height : height - 100}px`, '& .MuiDataGrid-row': { backgroundColor: 'comp.tableBg', color: 'text.primary', height: 'auto' }, '& .MuiDataGrid-columnHeader': { backgroundColor: 'comp.tableHeaderBg', color: 'text.primary' } }}
                    // sx={{ height: `${height - 100}px`, '& .MuiDataGrid-row': {backgroundColor: 'comp.tableBg', color: 'text.primary', height: 'auto' }, '& .MuiDataGrid-columnHeader': {backgroundColor: 'comp.tableHeaderBg', color: 'text.primary'}}}
                    localeText={LocaleTextDataGrid}
                    getRowId={(row) => row._id}
                    onCellDoubleClick={cotizacion ? (row) => handleAdd(row.id) : (row) => handleShowDetails(row.id)}
                    columns={[
                        { field: 'clave', headerName: 'Clave', maxWidth: 180, minWidth: 150, flex: 1 },
                        // { field: 'cantidad', headerName: 'Existencia', type: 'number', maxWidth: 80, cellClassName: (params) => params.value <= 0 ? 'text-danger text-bold h5' : 'text-success text-bold h5'},
                        { field: 'cantidad', headerName: 'Existencia', type: 'number', maxWidth: 80, cellClassName: (params) => params.value > 10 ? 'text-success text-bold h5' : params.value <= 0 ? 'text-danger text-bold h5' : 'text-warning text-bold h5' },
                        { field: 'nombre', headerName: 'Producto', minWidth: 200, flex: 1 },
                        { field: 'descripcion', headerName: 'Descripción', minWidth: 200, flex: 1 },
                        { field: 'marca', headerName: 'Marca' },
                        { field: 'modelo', headerName: 'Modelo', minWidth: 200, },
                        { field: 'categoria', headerName: 'Categoría', minWidth: 120, },
                        {
                            field: 'id',
                            headerName: 'Acción',
                            headerAlign: 'center',
                            align: 'center',
                            sortable: false,
                            filterable: false,
                            disableColumnMenu: true,
                            renderCell: (row) => (
                                <>
                                    {cotizacion ?
                                        <Fab variant='contained' color='primary' size='small' onClick={() => handleAdd(row.id)}>
                                            <i className='fad fa-plus-circle fa-2x'></i>
                                        </Fab> :
                                        !['Tecnico', 'Vendedor'].includes(auth.user.rol) ?
                                            <Link target='_blank' to={{ pathname: '/inventario/editarProducto/' + row.id, state: { id: row.id } }} style={{ textDecoration: 'none' }}>
                                                <Button sx={{ minWidth: '90px' }} variant='contained' color='primary' size='small'>
                                                    Editar &nbsp;&nbsp;<i className='fad fa-info-circle'></i>
                                                </Button>
                                            </Link> : <></>
                                    }
                                </>
                            )
                        },
                        {
                            field: 'imagen',
                            headerName: 'Imagen',
                            headerAlign: 'center',
                            align: 'center',
                            sortable: false,
                            filterable: false,
                            disableColumnMenu: true,
                            renderCell: (row) => (
                                <>
                                    <LazyLoadImage src={row.row.imagen} alt={row.row.nombre} style={{ width: '100px', height: '100px', objectFit: 'contain' }} />
                                </>
                            )
                        },
                    ]}
                    pagination
                    slots={{
                        pagination: CustomPagination,
                    }}
                    initialState={{
                        ...data.initialState,
                        pagination: { paginationModel: { pageSize: 25 } },
                    }}
                />
            </>
        )
    }

    const handleCloseDetails = () => {
        setShowDetails(false);
    };

    const renderDetails = () => {
        return (
            <Dialog
                fullWidth={true}
                maxWidth={'md'}
                className='exo2'
                scroll='paper'
                onClose={handleCloseDetails}
                open={showDetails} >
                <DialogTitle>Detalles del producto</DialogTitle>
                <DialogContent>
                    <Box sx={{ width: '100%', padding: '24px', border: 'solid 1px comp.boxBorder', backgroundColor: 'comp.boxBg', borderRadius: '20px' }}>
                        <Box className='d-flex align-items-stretch justify-content-center mb-2'>
                            <Typography sx={{ fontSize: '1.1em', color: 'comp.spColor2' }} className='px-1 d-flex align-items-center'>Nombre del producto:</Typography>
                            <Typography sx={{ fontSize: '1.1em' }} className='px-1 text-bold'>{data.nombre ? data.nombre : ''}</Typography>
                        </Box>
                        <Grid container spacing={3}>
                            <Grid item xs={12} >
                                <Box style={{ display: 'flex', justifyContent: 'center' }}>
                                    <img alt='product-img' style={{ width: 'auto', maxWidth: '400px', maxHeight: '300px', boxShadow: '2px 2px 10px #13161B', borderRadius: '20px' }} src={data.imagen} />
                                </Box>
                                <Box className='d-flex align-items-stretch justify-content-center'>
                                    <Typography sx={{ fontSize: '16px', color: 'comp.spColor1' }} className='my-3 px-1 d-flex align-items-center'>Unidades disponibles:</Typography>
                                    <Typography sx={{ fontSize: '16px' }} className='my-3 px-1 text-bold'>{data.cantidad ? data.cantidad + ' ' + data.unidad : ''}</Typography>
                                </Box>

                                <Divider className='my-3' textAlign="left">
                                    <CustomChip variant='outlined' estatus='DIAGNOSTICADO' label='Detalles del producto' className='text-bold' />
                                </Divider>

                                <Box >
                                    <Typography sx={{ fontSize: '16px', color: 'comp.spColor1' }} className='my-3 px-1 d-flex align-items-center'>Descripción:</Typography>
                                    <Typography sx={{ fontSize: '16px' }} className='my-3 px-1 text-bold'>{data.descripcion ? data.descripcion : ''}</Typography>
                                </Box>
                                <TableContainer>
                                    <Table>
                                        <TableBody>
                                            <TableRow>
                                                <TableCell sx={{ fontSize: '16px', color: 'comp.spColor2' }}>Clave del Producto:</TableCell>
                                                <TableCell sx={{ fontSize: '16px' }} className='text-bold'>{data.clave ? data.clave : ''}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontSize: '16px', color: 'comp.spColor2' }}>Categoría:</TableCell>
                                                <TableCell sx={{ fontSize: '16px' }} className='text-bold'>{data.categoria ? data.categoria : ''}</TableCell>
                                            </TableRow>
                                            {
                                                ['Tecnico'].includes(auth.user.rol) ? <></> :
                                                    <>
                                                        {
                                                            ['Vendedor', 'Secretaria'].includes(auth.user.rol) ? <></> :
                                                                <TableRow>
                                                                    <TableCell sx={{ fontSize: '16px', color: 'comp.spColor2' }}>Costo Unitario:</TableCell>
                                                                    <TableCell sx={{ fontSize: '16px' }} className='text-bold'>{data.costoUnitario ? `$ ${formatMoney(data.costoUnitario)}` : ''}</TableCell>
                                                                </TableRow>
                                                        }
                                                        <TableRow>
                                                            <TableCell sx={{ fontSize: '16px', color: 'comp.spColor2' }}>Precio Unitario:</TableCell>
                                                            <TableCell sx={{ fontSize: '16px' }} className='text-bold'>{data.precioUnitario ? `$ ${formatMoney(data.precioUnitario)} ⟶ ( $ ${formatMoney(data.precioUnitario * 1.16)} )` : ''}</TableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <TableCell sx={{ fontSize: '16px', color: 'comp.spColor2' }}>Precio Distribuidor:</TableCell>
                                                            <TableCell sx={{ fontSize: '16px' }} className='text-bold'>{data.precioDistribuidor ? `$ ${formatMoney(data.precioDistribuidor)} ⟶ ( $ ${formatMoney(data.precioDistribuidor * 1.16)} )` : ''}</TableCell>
                                                        </TableRow>
                                                    </>
                                            }
                                            <TableRow>
                                                <TableCell sx={{ fontSize: '16px', color: 'comp.spColor2' }}>Cantidad disponible:</TableCell>
                                                <TableCell sx={{ fontSize: '16px' }} className='text-bold'>{data.cantidad ? data.cantidad + ' ' + data.unidad : ''}</TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Grid>
                            <Grid item xs={12}>
                                <Divider className='my-3' textAlign="left">
                                    <CustomChip variant='outlined' estatus='DIAGNOSTICADO' label='Caracteristicas del producto' className='text-bold' />
                                </Divider>
                                <TableContainer>
                                    <Table>
                                        <TableBody>
                                            <TableRow>
                                                <TableCell sx={{ fontSize: '16px', color: 'comp.spColor2' }}>Dimensiones:</TableCell>
                                                <TableCell sx={{ fontSize: '16px' }} className='text-bold'>{`${data.largo ? data.largo : '? '} x ${data.ancho ? data.ancho : '?'} x ${data.alto ? data.alto : '?'}`}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontSize: '16px', color: 'comp.spColor2' }}>Proveedor:</TableCell>
                                                <TableCell sx={{ fontSize: '16px' }} className='text-bold'>{data.proveedor ? data.proveedor : ''}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontSize: '16px', color: 'comp.spColor2' }}>Marca:</TableCell>
                                                <TableCell sx={{ fontSize: '16px' }} className='text-bold'>{data.marca ? data.marca : ''}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontSize: '16px', color: 'comp.spColor2' }}>Modelo:</TableCell>
                                                <TableCell sx={{ fontSize: '16px' }} className='text-bold'>{data.modelo ? data.modelo : ''}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontSize: '16px', color: 'comp.spColor2' }}>No. Serie:</TableCell>
                                                <TableCell sx={{ fontSize: '16px' }} className='text-bold'>{data.noSerie ? data.noSerie : ''}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontSize: '16px', color: 'comp.spColor2' }}>No. Inventario:</TableCell>
                                                <TableCell sx={{ fontSize: '16px' }} className='text-bold'>{data.noInventario ? data.noInventario : ''}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontSize: '16px', color: 'comp.spColor2' }}>Unidad:</TableCell>
                                                <TableCell sx={{ fontSize: '16px' }} className='text-bold'>{data.unidad ? data.unidad : ''}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontSize: '16px', color: 'comp.spColor2' }}>Imagen:</TableCell>
                                                <TableCell sx={{ fontSize: '16px' }} className='text-bold'>{data.imagen ? data.imagen : ''}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontSize: '16px', color: 'comp.spColor2' }}>Caducidad:</TableCell>
                                                <TableCell sx={{ fontSize: '16px' }} className='text-bold'>{data.caducidad ? data.caducidad : ''}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontSize: '16px', color: 'comp.spColor2' }}>Observaciones:</TableCell>
                                                <TableCell sx={{ fontSize: '16px' }} className='text-bold'>{data.observaciones ? data.observaciones : ''}</TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Grid>
                            {
                                ['Tecnico', 'Vendedor', 'Secretaria'].includes(auth.user.rol) ?
                                    <Grid item xs={12}>
                                        <Divider className='my-3' textAlign="left">
                                            <CustomChip variant='outlined' estatus='DIAGNOSTICADO' label='Ubicación en almacén' className='text-bold' />
                                        </Divider>
                                        <Box className='d-flex align-items-stretch'>
                                            <TableContainer>
                                                <Table>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell sx={{ fontSize: '16px' }} className='text-center'>Almacén</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        <TableRow>
                                                            <TableCell sx={{ fontSize: '16px', color: 'comp.spColor1' }} className='text-bold text-center'>{data.ubicacion.almacen ? data.ubicacion.almacen : ''}</TableCell>
                                                        </TableRow>
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Box>
                                    </Grid> :
                                    <Grid item xs={12}>
                                        <Divider className='my-3' textAlign="left">
                                            <CustomChip variant='outlined' estatus='DIAGNOSTICADO' label='Ubicación en almacén' className='text-bold' />
                                        </Divider>
                                        <Box className='d-flex align-items-stretch'>
                                            <TableContainer>
                                                <Table>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell sx={{ fontSize: '16px' }} className='text-center'>Almacén</TableCell>
                                                            <TableCell sx={{ fontSize: '16px' }} className='text-center'>Anaquel</TableCell>
                                                            <TableCell sx={{ fontSize: '16px' }} className='text-center'>Nivel</TableCell>
                                                            <TableCell sx={{ fontSize: '16px' }} className='text-center'>Posición</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        <TableRow>
                                                            <TableCell sx={{ fontSize: '16px', color: 'comp.spColor1' }} className='text-bold text-center'>{data.ubicacion.almacen ? data.ubicacion.almacen : ''}</TableCell>
                                                            <TableCell sx={{ fontSize: '16px', color: 'comp.spColor1' }} className='text-bold text-center'>{data.ubicacion.anaquel ? data.ubicacion.anaquel : ''}</TableCell>
                                                            <TableCell sx={{ fontSize: '16px', color: 'comp.spColor1' }} className='text-bold text-center'>{data.ubicacion.nivel ? data.ubicacion.nivel : ''}</TableCell>
                                                            <TableCell sx={{ fontSize: '16px', color: 'comp.spColor1' }} className='text-bold text-center'>{data.ubicacion.posicion ? data.ubicacion.posicion : ''}</TableCell>
                                                        </TableRow>
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Box>
                                    </Grid>
                            }
                        </Grid>
                        <p></p>
                    </Box>
                </DialogContent>
                <DialogActions style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
                    {
                        ['Tecnico', 'Vendedor'].includes(auth.user.rol) ? <></> :
                            <>
                                <Link target='_blank' to={{ pathname: '/inventario/editarProducto/' + data._id, state: { id: data._id } }} style={{ textDecoration: 'none' }}>
                                    <Button sx={{ minWidth: '90px' }} variant='contained' color='primary' >
                                        Editar &nbsp;&nbsp;<i className='fad fa-edit'></i>
                                    </Button>
                                </Link>
                                <Link target='_blank' to={{ pathname: '/inventario/seguimientoProducto/' + data._id, state: { id: data._id } }} style={{ textDecoration: 'none' }}>
                                    <Button className='mx-3 my-2' sx={{ minWidth: '90px' }} variant='contained' color='info' >
                                        Ver historial de movimientos &nbsp;&nbsp;<i className='fad fa-history'></i>
                                    </Button>
                                </Link>
                            </>
                    }
                    <Button className='mx-2' color='warning' variant='contained' onClick={() => setShowDetails(false)}>Cerrar &nbsp; <i className="fad fa-times"></i></Button>
                </DialogActions>
            </Dialog>
        )
    }

    useEffect(() => {
        const handleKeyDown = e => {
            if (e.keyCode === 8) {
                // setSearchTerm('');
                document.getElementById('search').focus();
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [])

    const searchProducts = (e, text) => {
        e.preventDefault();
        setSearchTerm(text)
        getProducts(text);
    }

    const handleCloseVerification = () => {
        setShowVerification(false);
    }

    const confirmAddProduct = () => {
        return (
            <Dialog
                open={showVerification}
                onClose={() => handleCloseVerification()}
                fullWidth={true}
                maxWidth={'md'}
                scroll='paper'
            >
                <DialogTitle >Aviso</DialogTitle>
                <DialogContent>
                    <Typography variant='body1'>El producto que deseas agregar no cuenta con existencias en almacén ¿Estás seguro que deseas agregarlo?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button color='primary' variant='contained' onClick={() => forceAddProduct(currentId)}>Agregar &nbsp; <i className="fad fa-plus"></i></Button>
                    <Button color='warning' variant='contained' onClick={() => handleCloseVerification()}>Cerrar &nbsp; <i className="fad fa-times"></i></Button>
                </DialogActions>
            </Dialog>

        )
    }

    return (
        <Container maxWidth='xxl' style={{ marginTop: '10px', padding: '0px 8px' }}>
            <Paper sx={{ padding: '24px 0px' }} elevation={3}>
                <Container maxWidth='xxl' style={{ padding: '8px' }}>
                    {
                        cotizacion ? <></> :
                            <>
                                <Box className='text-center' >
                                    <Typography variant='h5' style={{ fontWeight: 700 }}>Consulta de Productos</Typography>
                                </Box>

                                <Container maxWidth='xl'>
                                    <Button size='small' variant='outlined' onClick={() => navigate('/inventario')}><i className='fad fa-arrow-left'></i> &nbsp;&nbsp;Regresar</Button>
                                </Container>

                                <Divider className='my-3' textAlign="center">
                                    <CustomChip variant='outlined' estatus='AUTORIZADO' label='Buscador de productos' className='text-bold' />
                                </Divider>
                            </>
                    }

                    <form onSubmit={(e) => searchProducts(e, searchTerm)}>
                        <Grid container spacing={1}>
                            <Grid item xs={12}>
                                <FormControl required sx={{ m: 1, minWidth: 100 }} style={{ display: 'flex' }}>
                                    <TextField id='search' label="Buscar..." variant="outlined" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                {categorias.map((c, i) => {
                                    if (i % 2 === 0) {
                                        return <Chip key={i} label={c.nombre} onClick={(e) => searchProducts(e, c.value)} color='info' sx={{ margin: '2px 8px', fontWeight: '500' }} variant='outlined' />
                                    } else {
                                        return <Chip key={i} label={c.nombre} onClick={(e) => searchProducts(e, c.value)} color='secondary' sx={{ margin: '2px 8px', fontWeight: '500' }} variant='outlined' />
                                    }
                                })}
                            </Grid>
                            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'end' }}>
                                <Button variant="contained" color="secondary" type='submit'>Buscar &nbsp; <i className="fad fa-sign-in"></i></Button>
                            </Grid>
                        </Grid>
                    </form>

                    {searchResults.length > 0 ? renderProducts() : <Typography variant='h6' className='text-center' style={{ fontWeight: 700 }}>No se encontraron productos</Typography>}
                </Container>
            </Paper>
            <CustomMessage open={apiRes.show} message={apiRes.message} severity={apiRes.severity} handleCloseMessage={handleCloseMessage} />
            {showDetails ? renderDetails() : <></>}
            {confirmAddProduct()}
        </Container>
    )
}

function Pagination({ page, onPageChange, className }) {
    const apiRef = useGridApiContext();
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    return (
        <MuiPagination
            color="primary"
            className={className}
            count={pageCount}
            page={page + 1}
            onChange={(event, newPage) => {
                onPageChange(event, newPage - 1);
            }}
        />
    );
}

function CustomPagination(props) {
    return <GridPagination ActionsComponent={Pagination} {...props} />;
}

export default TablaProductos;